/**
 * Homepage premium testimonials: virtual ring track, true lazy images, preload queue,
 * depth classes, details sync, dots + arrows (no autoplay).
 * Touch swipe when viewport ≤749px. Mobile: HpCarouselRestore on link leave/return.
 *
 * Track (n≥2): [pre×DEPTH_PAD] + [real×n] + [post×DEPTH_PAD] = n + 2×DEPTH_PAD nodes.
 * DEPTH_PAD matches is-d3 (±3 cards on web). Mobile uses DEPTH_PAD_MOBILE (±1 peek).
 * Only slides within the active mount window get <img>; wrap strips via ensureWrapStripReady.
 */
(function () {
  var IMG_SIZES =
    '(max-width: 749px) min(calc((100vw - 3.5rem) / 1.2), 26rem), min(32rem, max(17.5rem, calc((100vw - 9rem) / 3)))';
  var CLICK_STEP_MS = 640;
  var RING_SNAP_MS = 650;
  /** Ring geometry + desktop depth (is-d1 … is-d3); unchanged on mobile */
  var DEPTH_PAD = 3;
  /** Mobile mount window: current + peek neighbors only */
  var DEPTH_PAD_MOBILE = 1;
  var PRELOAD_MAX_CONCURRENT = 3;
  var PRELOAD_MAX_CONCURRENT_MOBILE = 2;

  function carouselApi() {
    return window.HpCarouselRestore || null;
  }

  function mod(i, n) {
    return ((i % n) + n) % n;
  }

  function initRoot(root) {
    var jsonEl = document.getElementById(root.dataset.jsonId);
    if (!jsonEl) return;

    var data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    var track = root.querySelector('[data-hp-t-track]');
    var viewport = root.querySelector('[data-hp-t-viewport]');
    var details = root.querySelector('[data-hp-t-details]');
    var dots = [].slice.call(root.querySelectorAll('[data-hp-t-dot]'));
    var prev = root.querySelector('[data-hp-t-prev]');
    var next = root.querySelector('[data-hp-t-next]');
    var iconTpl = document.getElementById(root.dataset.iconTplId);

    if (!track || !viewport || !details || !data.slides) return;

    var n = data.slides.length;
    if (n < 2) return;

    var allSlides = [];
    var useRing = true;
    var physicalPos = 0;
    var logicalIdx = 0;
    var touchStartX = null;
    var touchStartY = null;
    var ringWrapAnimating = false;
    var ringSnapTimer = null;
    var clickStepTimer = null;
    var imageCache = Object.create(null);
    var preloadQueue = [];
    var preloadActive = 0;
    var layoutTrackRaf = null;
    var trackBuilt = false;
    var wrapCompleteWaiters = [];
    var wrapWarm = { next: null, prev: null };
    var wrapStripWarm = { next: null, prev: null };
    var mobilePendingBoundary = null;
    var mobileBoundaryNavScheduled = false;
    var wrapCompleting = false;
    var navChain = Promise.resolve();

    function firstRealPos() {
      return DEPTH_PAD;
    }

    function lastRealPos() {
      return DEPTH_PAD + n - 1;
    }

    function lastPreClonePos() {
      return DEPTH_PAD - 1;
    }

    function firstPostClonePos() {
      return DEPTH_PAD + n;
    }

    function logicalAtPhysical(p) {
      if (!useRing) return p;
      if (p < DEPTH_PAD) return mod(n - DEPTH_PAD + p, n);
      if (p < DEPTH_PAD + n) return p - DEPTH_PAD;
      return mod(p - (DEPTH_PAD + n), n);
    }

    function physToLog(p) {
      return logicalAtPhysical(p);
    }

    function logToPhys(l) {
      if (!useRing) return l;
      return DEPTH_PAD + mod(l, n);
    }

    function isClonePos(p) {
      return useRing && (p < DEPTH_PAD || p >= DEPTH_PAD + n);
    }

    function isTestimonialTouchSwipeViewport() {
      return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    }

    function effectiveMountPad() {
      return isTestimonialTouchSwipeViewport() ? DEPTH_PAD_MOBILE : DEPTH_PAD;
    }

    function preloadConcurrencyLimit() {
      return isTestimonialTouchSwipeViewport() ? PRELOAD_MAX_CONCURRENT_MOBILE : PRELOAD_MAX_CONCURRENT;
    }

    function shouldMountImageAt(p, centerPos) {
      return Math.abs(p - centerPos) <= effectiveMountPad();
    }

    function releaseSlideImage(slide) {
      if (!slide || slide._hpImagePromise) return;
      var media = slide._hpMedia;
      if (!media) return;
      media.style.backgroundImage = '';
      var img = media.querySelector('img');
      if (img) img.remove();
      removeLongTermBadge(media);
    }

    /** Mobile only: drop <img> outside ±DEPTH_PAD_MOBILE to cap memory (e.g. n=16). */
    function pruneImagesOutsideWindow(centerPos) {
      if (!isTestimonialTouchSwipeViewport()) return;
      if (ringWrapAnimating) return;
      var pad = DEPTH_PAD_MOBILE;
      var p;
      for (p = 0; p < allSlides.length; p++) {
        if (Math.abs(p - centerPos) <= pad) continue;
        releaseSlideImage(allSlides[p]);
      }
    }

    function drainPreloadQueue() {
      var limit = preloadConcurrencyLimit();
      while (preloadActive < limit && preloadQueue.length) {
        var job = preloadQueue.shift();
        preloadActive++;
        var probe = new Image();
        var imageData = job.imageData;
        if (imageData.srcset) probe.srcset = imageData.srcset;
        if (imageData.sizes) probe.sizes = imageData.sizes;
        var finish = function () {
          job.settle(probe);
          preloadActive--;
          drainPreloadQueue();
        };
        probe.onload = finish;
        probe.onerror = finish;
        probe.src = imageData.src;
      }
    }

    function isImageCached(imageData) {
      return !!(imageData && imageData.src && imageCache[imageData.src]);
    }

    function preloadImageData(imageData) {
      if (!imageData || !imageData.src) return Promise.resolve();
      var key = imageData.src;
      if (imageCache[key]) return imageCache[key];
      imageCache[key] = new Promise(function (resolve) {
        preloadQueue.push({ imageData: imageData, settle: resolve });
        drainPreloadQueue();
      });
      return imageCache[key];
    }

    function cancelScheduledLayoutTrack() {
      if (layoutTrackRaf != null && window.cancelAnimationFrame) {
        cancelAnimationFrame(layoutTrackRaf);
      }
      layoutTrackRaf = null;
    }

    function scheduleLayoutTrackIfCenter(p) {
      if (p !== physicalPos) return;
      if (ringWrapAnimating) return;
      if (layoutTrackRaf != null) return;
      if (window.requestAnimationFrame) {
        layoutTrackRaf = requestAnimationFrame(function () {
          layoutTrackRaf = null;
          if (p !== physicalPos || ringWrapAnimating) return;
          layoutTrack();
        });
      } else {
        layoutTrack();
      }
    }

    /** Sync logical index on shell only — no network, no <img>. */
    function bindSlideLogical(slide, logicalIndex) {
      var log = mod(logicalIndex, n);
      slide.setAttribute('data-hp-t-logical', String(log));
      slide._hpLogical = log;
    }

    /**
     * Mount/decode images for the active mount window around centerPos.
     * Desktop ±DEPTH_PAD; mobile ±DEPTH_PAD_MOBILE. Outside = no fetch.
     */
    function mountWindowAtCenter(centerPos, opts) {
      opts = opts || {};
      if (!trackBuilt) buildVirtualTrack();
      var jobs = [];
      var p;
      for (p = 0; p < allSlides.length; p++) {
        var log = logicalAtPhysical(p);
        bindSlideLogical(allSlides[p], log);
        if (!shouldMountImageAt(p, centerPos)) continue;
        jobs.push(
          assignSlideLogical(allSlides[p], log, {
            eager: true,
            fetchPriority: p === centerPos ? 'high' : null,
            physicalPos: p,
            centerPos: centerPos,
            wrapPrep: !!opts.wrapPrep
          })
        );
      }
      return Promise.all(jobs).then(function () {
        if (!opts.skipPrune) pruneImagesOutsideWindow(centerPos);
      });
    }

    function prepareImagesAtCenter(centerPos, opts) {
      return mountWindowAtCenter(centerPos, opts || {});
    }

    /** Before nav: ensure target window slides are mounted and decoded. */
    function ensurePhysicalWindowReady(centerPos) {
      return prepareImagesAtCenter(centerPos);
    }

    /** Pre-mount snap destination (first/last real) so mobile wrap does not reload with blur. */
    function ensureWrapDestinationReady(direction) {
      var destPos = direction === 'next' ? firstRealPos() : lastRealPos();
      return mountWindowAtCenter(destPos, { skipPrune: true, wrapPrep: true });
    }

    function ensureWrapReady(direction) {
      return Promise.all([ensureWrapStripReady(direction), ensureWrapDestinationReady(direction)]);
    }

    function warmWrap(direction) {
      if (wrapWarm[direction]) return wrapWarm[direction];
      wrapWarm[direction] = ensureWrapReady(direction).finally(function () {
        wrapWarm[direction] = null;
      });
      return wrapWarm[direction];
    }

    function warmWrapStrip(direction) {
      if (wrapStripWarm[direction]) return wrapStripWarm[direction];
      wrapStripWarm[direction] = ensureWrapStripReady(direction).finally(function () {
        wrapStripWarm[direction] = null;
      });
      return wrapStripWarm[direction];
    }

    /** Mobile: start wrap animation after strip is ready; load snap target during transition. */
    function runMobileBoundaryWrap(direction) {
      var destReady = ensureWrapDestinationReady(direction);
      return warmWrapStrip(direction).then(function () {
        physicalPos = direction === 'next' ? firstPostClonePos() : lastPreClonePos();
        ringWrapAnimating = true;
        return commitVisualState().then(function () {
          scheduleRingSnapFallback();
          return Promise.all([waitForWrapComplete(), destReady]);
        });
      });
    }

    function isBoundaryWrapDirection(direction) {
      return (
        (direction === 'next' && physicalPos === lastRealPos()) ||
        (direction === 'prev' && physicalPos === firstRealPos())
      );
    }

    /** Mobile: warm wrap assets one step before boundary so fast swipes are not dropped. */
    function prefetchWrapIfApproachingBoundary(centerPos) {
      if (!isTestimonialTouchSwipeViewport()) return;
      if (centerPos === lastRealPos() - 1 || centerPos === lastRealPos()) {
        warmWrapStrip('next');
        if (centerPos === lastRealPos()) warmWrap('next');
      }
      if (centerPos === firstRealPos() + 1 || centerPos === firstRealPos()) {
        warmWrapStrip('prev');
        if (centerPos === firstRealPos()) warmWrap('prev');
      }
    }

    function ensureWrapStripReady(direction) {
      var jobs = [];
      var i;
      if (direction === 'next') {
        for (i = 0; i < DEPTH_PAD; i++) {
          var postP = firstPostClonePos() + i;
          jobs.push(
            assignSlideLogical(allSlides[postP], i, {
              eager: true,
              physicalPos: postP,
              centerPos: physicalPos
            })
          );
        }
      } else {
        for (i = 0; i < DEPTH_PAD; i++) {
          jobs.push(
            assignSlideLogical(allSlides[i], logicalAtPhysical(i), {
              eager: true,
              physicalPos: i,
              centerPos: physicalPos
            })
          );
        }
      }
      return Promise.all(jobs);
    }

    function removeLongTermBadge(mediaEl) {
      [].slice.call(mediaEl.querySelectorAll('.hp-testimonials__long-term-badge')).forEach(function (el) {
        el.remove();
      });
    }

    function createLongTermBadge() {
      var badge = document.createElement('span');
      badge.className = 'hp-testimonials__long-term-badge';

      var text = document.createElement('span');
      text.appendChild(document.createTextNode('1-YEAR LONG-TERM REVIEW'));
      badge.appendChild(text);

      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'hp-testimonials__long-term-shield');
      svg.setAttribute('viewBox', '0 0 24 28');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');

      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M12 2.6 20.2 6v7.2c0 5.6-3.25 9.7-8.2 12.2-4.95-2.5-8.2-6.6-8.2-12.2V6L12 2.6Z');
      svg.appendChild(path);
      badge.appendChild(svg);

      return badge;
    }

    function applyImageToMedia(mediaEl, logical, opts) {
      opts = opts || {};
      var item = data.slides[mod(logical, n)];
      mediaEl.style.backgroundImage = '';
      var existing = mediaEl.querySelector('img');
      if (existing) existing.remove();
      removeLongTermBadge(mediaEl);
      [].slice.call(mediaEl.querySelectorAll('.placeholder-svg')).forEach(function (el) {
        el.remove();
      });

      if (!item || !item.image) {
        return Promise.resolve();
      }

      var imageData = item.image;
      var img = document.createElement('img');
      img.alt = imageData.alt || 'Testimonial';
      if (imageData.width) img.width = imageData.width;
      if (imageData.height) img.height = imageData.height;
      if (imageData.srcset) img.setAttribute('srcset', imageData.srcset);
      img.setAttribute('sizes', imageData.sizes || IMG_SIZES);
      img.setAttribute('loading', opts.eager ? 'eager' : 'lazy');
      if (opts.fetchPriority) img.setAttribute('fetchpriority', opts.fetchPriority);
      img.classList.add('hp-testimonials__img');
      var cached = isImageCached(imageData);
      var instantReveal = !!opts.wrapPrep || (isTestimonialTouchSwipeViewport() && cached);
      var usePlaceholder =
        imageData.placeholder &&
        !instantReveal &&
        (!isTestimonialTouchSwipeViewport() || opts.physicalPos === opts.centerPos);
      if (usePlaceholder) {
        mediaEl.style.backgroundImage = 'url(' + imageData.placeholder + ')';
        mediaEl.style.backgroundSize = 'cover';
        mediaEl.style.backgroundPosition = 'center';
        img.classList.add('hp-testimonials__img--loading');
      } else if (instantReveal) {
        img.style.opacity = '0';
      } else {
        img.classList.add('hp-testimonials__img--loading');
      }
      mediaEl.appendChild(img);
      if (item.is_long_term) {
        mediaEl.appendChild(createLongTermBadge());
      }

      return preloadImageData(imageData).then(function () {
        img.src = imageData.src;
        var done = function () {
          img.classList.remove('hp-testimonials__img--loading');
          img.classList.add('hp-testimonials__img--loaded');
          if (instantReveal) {
            img.style.transition = 'none';
            img.style.opacity = '1';
            void img.offsetWidth;
            img.style.transition = '';
            img.style.opacity = '';
          }
          if (typeof opts.physicalPos === 'number') {
            scheduleLayoutTrackIfCenter(opts.physicalPos);
          }
        };
        if (instantReveal && img.complete) {
          done();
          return undefined;
        }
        if (img.decode) {
          return img.decode().then(done).catch(done);
        }
        done();
        return undefined;
      });
    }

    function createSlideShell(physicalIndex, logicalIndex, role) {
      var slide = document.createElement('div');
      slide.className = 'hp-testimonials__slide';
      slide.setAttribute('data-hp-t-logical', String(logicalIndex));
      if (role === 'pre') {
        slide.setAttribute('data-hp-t-ring', 'pre');
        slide.setAttribute('aria-hidden', 'true');
      } else if (role === 'post') {
        slide.setAttribute('data-hp-t-ring', 'post');
        slide.setAttribute('aria-hidden', 'true');
      } else {
        slide.setAttribute('data-hp-t-slide', '');
      }
      var card = document.createElement('div');
      card.className = 'hp-testimonials__card';
      var media = document.createElement('div');
      media.className = 'hp-testimonials__media';
      card.appendChild(media);
      slide.appendChild(card);
      slide._hpMedia = media;
      slide._hpLogical = logicalIndex;
      return slide;
    }

    function assignSlideLogical(slide, logicalIndex, opts) {
      opts = opts || {};
      var log = mod(logicalIndex, n);
      bindSlideLogical(slide, log);
      if (slide._hpImagePromise) return slide._hpImagePromise;
      var existingImg = slide._hpMedia.querySelector('img.hp-testimonials__img--loaded');
      if (existingImg) {
        if (opts.eager) existingImg.setAttribute('loading', 'eager');
        return Promise.resolve();
      }
      slide._hpImagePromise = applyImageToMedia(slide._hpMedia, log, opts).finally(function () {
        slide._hpImagePromise = null;
      });
      return slide._hpImagePromise;
    }

    function buildVirtualTrack() {
      track.innerHTML = '';
      allSlides = [];
      var i;
      for (i = 0; i < DEPTH_PAD; i++) {
        var pre = createSlideShell(i, logicalAtPhysical(i), 'pre');
        track.appendChild(pre);
        allSlides.push(pre);
      }
      for (i = 0; i < n; i++) {
        var real = createSlideShell(DEPTH_PAD + i, i, 'real');
        track.appendChild(real);
        allSlides.push(real);
      }
      for (i = 0; i < DEPTH_PAD; i++) {
        var post = createSlideShell(DEPTH_PAD + n + i, logicalAtPhysical(DEPTH_PAD + n + i), 'post');
        track.appendChild(post);
        allSlides.push(post);
      }
      trackBuilt = true;
    }

    function setTrackTransition(enabled) {
      track.style.transition = enabled ? '' : 'none';
    }

    function getTrackMetrics(pos) {
      var p = typeof pos === 'number' ? pos : physicalPos;
      var slide = allSlides[p];
      if (!slide) return null;
      var measureSlide = useRing && allSlides[firstRealPos()] ? allSlides[firstRealPos()] : slide;
      var w = measureSlide.offsetWidth;
      if (!w) w = measureSlide.getBoundingClientRect().width;
      w = Math.round(w);
      if (!w) return null;
      var cs = window.getComputedStyle(track);
      var gap = Math.round(parseFloat(cs.columnGap || cs.gap) || 16);
      var vw = Math.round(viewport.getBoundingClientRect().width);
      if (!vw) return null;
      return {
        width: w,
        gap: gap,
        step: w + gap,
        viewportWidth: vw
      };
    }

    function getTranslateForPhysical(pos) {
      var metrics = getTrackMetrics(pos);
      if (!metrics) return 0;
      return Math.round(metrics.viewportWidth / 2 - (pos * metrics.step + metrics.width / 2));
    }

    function restoreSlideTransitions() {
      allSlides.forEach(function (s) {
        s.style.transition = '';
      });
    }

    function layoutTrack(opts) {
      opts = opts || {};
      var slide = allSlides[physicalPos];
      if (!slide) return false;
      if (!getTrackMetrics(physicalPos)) {
        if (!opts._metricRetry && window.requestAnimationFrame) {
          requestAnimationFrame(function () {
            layoutTrack(Object.assign({}, opts, { _metricRetry: true }));
          });
        }
        return false;
      }
      var instant = !!opts.noTransition;
      var suppressSlideTransition = !!(instant && allSlides.length);
      if (instant) {
        setTrackTransition(false);
        if (suppressSlideTransition) {
          allSlides.forEach(function (s) {
            s.style.transition = 'none';
          });
        }
      } else {
        setTrackTransition(true);
      }
      var tx = getTranslateForPhysical(physicalPos);
      track.style.transform = 'translate3d(' + tx + 'px, 0, 0)';
      applyDepth();
      if (instant) {
        void track.offsetWidth;
        if (suppressSlideTransition) restoreSlideTransitions();
        setTrackTransition(true);
      }
      return true;
    }

    /** Apply track transform first, then sync dots + details (prevents indicator/image desync). */
    function commitVisualState(opts) {
      opts = opts || {};
      cancelScheduledLayoutTrack();
      var ok = layoutTrack(opts);
      if (!ok) {
        return new Promise(function (resolve) {
          var tries = 0;
          function retry() {
            tries++;
            if (layoutTrack(opts) || tries >= 4) {
              updateDots();
              renderDetails(physToLog(physicalPos));
              resolve();
              return;
            }
            requestAnimationFrame(retry);
          }
          requestAnimationFrame(retry);
        });
      }
      updateDots();
      renderDetails(physToLog(physicalPos));
      return Promise.resolve();
    }

    function applyDepth() {
      allSlides.forEach(function (slide, p) {
        var dPhys = Math.abs(p - physicalPos);
        slide.classList.remove('is-active', 'is-d1', 'is-d2', 'is-d3');
        if (p === physicalPos) slide.classList.add('is-active');
        else if (dPhys === 1) slide.classList.add('is-d1');
        else if (dPhys === 2) slide.classList.add('is-d2');
        else slide.classList.add('is-d3');
      });
    }

    function updateDots() {
      logicalIdx = physToLog(physicalPos);
      dots.forEach(function (d, j) {
        d.classList.toggle('is-active', j === logicalIdx);
        d.setAttribute('aria-selected', j === logicalIdx ? 'true' : 'false');
      });
    }

    function renderDetails(i) {
      var item = data.slides[i];
      if (!item) return;

      details.innerHTML = '';
      details.setAttribute('data-active-index', String(i));

      var quoteText = (item.quote || '').replace(/^\s+|\s+$/g, '');
      if (quoteText) {
        var q = document.createElement('blockquote');
        q.className = 'hp-testimonials__quote';
        q.appendChild(document.createTextNode(quoteText));
        details.appendChild(q);
      }

      var meta = document.createElement('div');
      meta.className = 'hp-testimonials__meta hp-testimonials__meta--story-only';

      if (item.link_label && item.link_url) {
        var a = document.createElement('a');
        a.className = 'hp-btn hp-btn--ghost hp-testimonials__story-btn';
        a.href = item.link_url;
        if (iconTpl && iconTpl.content) {
          var iconWrap = document.createElement('span');
          iconWrap.className = 'hp-testimonials__story-icon';
          iconWrap.appendChild(document.importNode(iconTpl.content, true));
          a.appendChild(iconWrap);
        }
        var label = document.createElement('span');
        label.className = 'hp-testimonials__story-label';
        label.textContent = item.link_label;
        a.appendChild(label);
        meta.appendChild(a);
      }

      if (meta.childNodes.length) {
        details.appendChild(meta);
      }
    }

    function clearRingSnapTimer() {
      if (ringSnapTimer) {
        clearTimeout(ringSnapTimer);
        ringSnapTimer = null;
      }
    }

    function cancelRingWrap() {
      ringWrapAnimating = false;
      clearRingSnapTimer();
    }

    function cancelClickStepQueue() {
      if (clickStepTimer) {
        clearTimeout(clickStepTimer);
        clickStepTimer = null;
      }
    }

    function flushWrapWaiters() {
      var list = wrapCompleteWaiters.slice();
      wrapCompleteWaiters = [];
      list.forEach(function (fn) {
        fn();
      });
    }

    function snapRingIfAtClone() {
      if (!useRing) {
        cancelRingWrap();
        return Promise.resolve(false);
      }
      var snapped = false;
      if (physicalPos < DEPTH_PAD) {
        physicalPos += n;
        snapped = true;
      } else if (physicalPos >= DEPTH_PAD + n) {
        physicalPos -= n;
        snapped = true;
      }
      if (!snapped) return Promise.resolve(false);
      return commitVisualState({ noTransition: true })
        .then(function () {
          return ensurePhysicalWindowReady(physicalPos);
        })
        .then(function () {
          return commitVisualState({ noTransition: true });
        });
    }

    function notifyWrapComplete() {
      if (!ringWrapAnimating || wrapCompleting) return;
      wrapCompleting = true;
      clearRingSnapTimer();
      snapRingIfAtClone().then(function () {
        wrapCompleting = false;
        cancelRingWrap();
        flushWrapWaiters();
      });
    }

    function scheduleRingSnapFallback() {
      clearRingSnapTimer();
      ringSnapTimer = setTimeout(function () {
        ringSnapTimer = null;
        notifyWrapComplete();
      }, RING_SNAP_MS);
    }

    function onTrackTransitionEnd(e) {
      if (!useRing || !ringWrapAnimating) return;
      if (e.target !== track) return;
      if (e.propertyName && e.propertyName !== 'transform') return;
      notifyWrapComplete();
    }

    function waitForSlideTransition() {
      return new Promise(function (resolve) {
        if (track.style.transition === 'none') {
          resolve();
          return;
        }
        var settled = false;
        var timer = null;
        function finish() {
          if (settled) return;
          settled = true;
          track.removeEventListener('transitionend', onEnd);
          if (timer) clearTimeout(timer);
          resolve();
        }
        function onEnd(ev) {
          if (ev.target !== track) return;
          if (ev.propertyName && ev.propertyName !== 'transform') return;
          finish();
        }
        track.addEventListener('transitionend', onEnd);
        timer = setTimeout(finish, 640);
      });
    }

    function waitForWrapComplete() {
      if (!ringWrapAnimating) return Promise.resolve();
      return new Promise(function (resolve) {
        wrapCompleteWaiters.push(resolve);
      });
    }

    function isWebCarouselNav() {
      return !isTestimonialTouchSwipeViewport();
    }

    function scheduleMobileBoundaryNav() {
      if (mobileBoundaryNavScheduled) return;
      mobileBoundaryNavScheduled = true;
      navChain = navChain
        .then(function () {
          mobileBoundaryNavScheduled = false;
          var dir = mobilePendingBoundary;
          mobilePendingBoundary = null;
          if (!dir) return;
          if (dir === 'prev' && physicalPos !== firstRealPos()) return;
          if (dir === 'next' && physicalPos !== lastRealPos()) return;
          return runNavStep(dir);
        })
        .catch(function () {
          mobileBoundaryNavScheduled = false;
          return undefined;
        });
    }

    function queueNavStep(direction) {
      if (!isWebCarouselNav() && isBoundaryWrapDirection(direction)) {
        mobilePendingBoundary = direction;
        scheduleMobileBoundaryNav();
        return;
      }
      navChain = navChain
        .then(function () {
          return runNavStep(direction);
        })
        .catch(function () {
          return undefined;
        });
    }

    /** One logical step; serialized on web so rapid arrow clicks cannot race wrap/snap. */
    function runNavStep(direction) {
      if (!useRing) {
        return goToLogicalAsync(logicalIdx + (direction === 'next' ? 1 : -1));
      }

      if (ringWrapAnimating) {
        return waitForWrapComplete().then(function () {
          return runNavStep(direction);
        });
      }

      if (isClonePos(physicalPos)) {
        return snapRingIfAtClone().then(function () {
          return runNavStep(direction);
        });
      }

      if (direction === 'next') {
        if (physicalPos < lastRealPos()) {
          var nextPos = physicalPos + 1;
          return ensurePhysicalWindowReady(nextPos).then(function () {
            physicalPos = nextPos;
            return commitVisualState().then(function () {
              prefetchWrapIfApproachingBoundary(physicalPos);
              if (isWebCarouselNav()) return waitForSlideTransition();
            });
          });
        }
        if (physicalPos === lastRealPos()) {
          if (isTestimonialTouchSwipeViewport()) {
            return runMobileBoundaryWrap('next');
          }
          return warmWrap('next').then(function () {
            physicalPos = firstPostClonePos();
            ringWrapAnimating = true;
            return commitVisualState().then(function () {
              scheduleRingSnapFallback();
              return waitForWrapComplete();
            });
          });
        }
        return Promise.resolve();
      }

      if (physicalPos > firstRealPos()) {
        var prevPos = physicalPos - 1;
        return ensurePhysicalWindowReady(prevPos).then(function () {
          physicalPos = prevPos;
          return commitVisualState().then(function () {
            prefetchWrapIfApproachingBoundary(physicalPos);
            if (isWebCarouselNav()) return waitForSlideTransition();
          });
        });
      }
      if (physicalPos === firstRealPos()) {
        if (isTestimonialTouchSwipeViewport()) {
          return runMobileBoundaryWrap('prev');
        }
        return warmWrap('prev').then(function () {
          physicalPos = lastPreClonePos();
          ringWrapAnimating = true;
          return commitVisualState().then(function () {
            scheduleRingSnapFallback();
            return waitForWrapComplete();
          });
        });
      }
      return Promise.resolve();
    }

    function goToLogicalAsync(target, opts) {
      opts = opts || {};
      cancelClickStepQueue();
      cancelRingWrap();
      logicalIdx = mod(target, n);
      physicalPos = logToPhys(logicalIdx);
      return prepareImagesAtCenter(physicalPos).then(function () {
        return commitVisualState(opts.instant ? { noTransition: true } : undefined);
      });
    }

    function goToLogical(target, opts) {
      goToLogicalAsync(target, opts);
    }

    function stepNext() {
      queueNavStep('next');
    }

    function stepPrev() {
      queueNavStep('prev');
    }

    function navigateToLogicalBySteps(toLog) {
      cancelClickStepQueue();
      if (!useRing) {
        goToLogical(toLog);
        return;
      }
      function queueStepsToTarget() {
        var from = physToLog(physicalPos);
        if (from === toLog) return;
        var df = mod(toLog - from, n);
        var db = mod(from - toLog, n);
        var forward = df <= db;
        var steps = forward ? df : db;
        var s;
        for (s = 0; s < steps; s++) {
          queueNavStep(forward ? 'next' : 'prev');
        }
      }

      if (isClonePos(physicalPos)) {
        snapRingIfAtClone().then(queueStepsToTarget);
        return;
      }
      queueStepsToTarget();
    }

    function rebuildLayout(opts) {
      opts = opts || {};
      cancelClickStepQueue();
      cancelRingWrap();
      navChain = Promise.resolve();
      mobilePendingBoundary = null;
      mobileBoundaryNavScheduled = false;
      wrapCompleting = false;
      logicalIdx = physToLog(physicalPos);

      if (!trackBuilt) {
        buildVirtualTrack();
        physicalPos = logToPhys(logicalIdx);
        return prepareImagesAtCenter(physicalPos).then(function () {
          return commitVisualState(opts.noTransition ? { noTransition: true } : undefined).then(function () {
            prefetchWrapIfApproachingBoundary(physicalPos);
          });
        });
      }

      physicalPos = logToPhys(logicalIdx);
      if (opts.refreshImages) {
        return prepareImagesAtCenter(physicalPos).then(function () {
          return commitVisualState(opts.noTransition ? { noTransition: true } : undefined);
        });
      }
      commitVisualState(opts.noTransition ? { noTransition: true } : undefined);
      return Promise.resolve();
    }

    function getCarouselId() {
      return root.id || null;
    }

    function getRestoreIndex() {
      var api = carouselApi();
      if (!api || !isTestimonialTouchSwipeViewport()) return null;
      var id = getCarouselId();
      if (!id || !api.shouldRestoreCarousel(id)) return null;
      var idx = api.readIndex(id);
      if (idx == null || idx >= n) return null;
      return idx;
    }

    function saveCarouselState() {
      var api = carouselApi();
      if (!api || !isTestimonialTouchSwipeViewport()) return;
      var id = getCarouselId();
      if (!id) return;
      api.saveForCarousel(id, logicalIdx);
    }

    function restoreCarouselStateIfNeeded() {
      var api = carouselApi();
      var target = getRestoreIndex();
      if (target == null) return false;
      if (target !== logicalIdx) {
        goToLogical(target, { instant: true });
      }
      if (api) api.clearPending();
      return true;
    }

    function bindCarouselLinkSave() {
      root.addEventListener(
        'click',
        function (e) {
          if (!isTestimonialTouchSwipeViewport()) return;
          if (e.button !== 0) return;
          var anchor = e.target.closest ? e.target.closest('a[href]') : null;
          var api = carouselApi();
          if (!api || !api.isNavigableLink(anchor)) return;
          if (!anchor.closest('[data-hp-carousel-save-zone]')) return;
          saveCarouselState();
        },
        true
      );
    }

    if (prev) {
      prev.addEventListener('click', function () {
        cancelClickStepQueue();
        stepPrev();
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        cancelClickStepQueue();
        stepNext();
      });
    }
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () {
        cancelClickStepQueue();
        if (useRing) navigateToLogicalBySteps(i);
        else goToLogical(i);
      });
    });

    viewport.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.target.closest('.hp-testimonials__nav-btn')) return;
      if (e.target.closest('[data-hp-t-dot]')) return;
      var stack = document.elementsFromPoint(e.clientX, e.clientY);
      var slidesInStack = [];
      var si;
      for (si = 0; si < stack.length; si++) {
        var node = stack[si];
        if (node && node.matches && node.matches('.hp-testimonials__slide')) {
          slidesInStack.push(node);
        }
      }
      if (!slidesInStack.length) return;
      var curSlide = allSlides[physicalPos];
      var hit = null;
      var h;
      for (h = 0; h < slidesInStack.length; h++) {
        if (slidesInStack[h] !== curSlide) {
          hit = slidesInStack[h];
          break;
        }
      }
      if (!hit) return;
      var i = allSlides.indexOf(hit);
      if (i === -1) return;
      if (useRing) {
        if (i === physicalPos - 1) {
          cancelClickStepQueue();
          stepPrev();
          return;
        }
        if (i === physicalPos + 1) {
          cancelClickStepQueue();
          stepNext();
          return;
        }
        cancelClickStepQueue();
        navigateToLogicalBySteps(physToLog(i));
      } else {
        goToLogical(physToLog(i));
      }
    });

    function resetTouchSwipe() {
      touchStartX = null;
      touchStartY = null;
    }

    function isTestimonialHorizontalSwipe(dx, dy) {
      if (Math.abs(dx) < 40) return false;
      return Math.abs(dx) > Math.abs(dy);
    }

    viewport.addEventListener(
      'touchstart',
      function (e) {
        if (!isTestimonialTouchSwipeViewport()) return;
        if (e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchcancel',
      function () {
        if (!isTestimonialTouchSwipeViewport()) return;
        resetTouchSwipe();
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      function (e) {
        if (!isTestimonialTouchSwipeViewport()) return;
        if (touchStartX == null || e.changedTouches.length !== 1) return;
        var x = e.changedTouches[0].clientX;
        var y = e.changedTouches[0].clientY;
        var dx = x - touchStartX;
        var dy = y - touchStartY;
        resetTouchSwipe();
        if (!isTestimonialHorizontalSwipe(dx, dy)) return;
        cancelClickStepQueue();
        if (dx < 0) stepNext();
        else stepPrev();
      },
      { passive: true }
    );

    var restoreIdx = getRestoreIndex();
    var restoring = restoreIdx != null;
    if (restoring) {
      root.setAttribute('data-hp-carousel-restoring', '');
    }

    logicalIdx = restoring ? restoreIdx : 0;
    physicalPos = logToPhys(logicalIdx);
    track.addEventListener('transitionend', onTrackTransitionEnd);
    bindCarouselLinkSave();

    rebuildLayout({ noTransition: restoring }).then(function () {
      var api = carouselApi();
      if (api) {
        api.scheduleRestore(function () {
          if (!isTestimonialTouchSwipeViewport()) return;
          if (restoreCarouselStateIfNeeded()) {
            root.removeAttribute('data-hp-carousel-restoring');
          }
        });
      }
      if (restoring) {
        if (api) api.clearPending();
        if (window.requestAnimationFrame) {
          requestAnimationFrame(function () {
            root.removeAttribute('data-hp-carousel-restoring');
          });
        } else {
          root.removeAttribute('data-hp-carousel-restoring');
        }
      }
    });

    var mountPadMq =
      window.matchMedia && window.matchMedia('(max-width: 749px)');
    var desktopMq = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;

    function onViewportMountPadChange() {
      rebuildLayout({ noTransition: true, refreshImages: true });
    }

    function onResize() {
      rebuildLayout({ noTransition: true, refreshImages: true });
    }

    function onDesktopMqChange() {
      rebuildLayout({ noTransition: true, refreshImages: true });
    }

    function bindMq(mq, handler) {
      if (!mq) return;
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else if (mq.addListener) mq.addListener(handler);
    }

    function unbindMq(mq, handler) {
      if (!mq) return;
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    }

    window.addEventListener('resize', onResize, { passive: true });
    bindMq(mountPadMq, onViewportMountPadChange);
    bindMq(desktopMq, onDesktopMqChange);

    /** Theme editor: tear down window/matchMedia listeners + timers when this section unloads. */
    function teardown() {
      window.removeEventListener('resize', onResize);
      unbindMq(mountPadMq, onViewportMountPadChange);
      unbindMq(desktopMq, onDesktopMqChange);
      clearRingSnapTimer();
      cancelClickStepQueue();
      cancelScheduledLayoutTrack();
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    }

    function onSectionUnload(e) {
      if (e && e.target && (e.target === root || (e.target.contains && e.target.contains(root)))) {
        teardown();
      }
    }

    document.addEventListener('shopify:section:unload', onSectionUnload);
  }

  function boot() {
    document.querySelectorAll('[data-hp-t-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
